import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  makeStyles,
  MessageBar,
  MessageBarBody,
  Spinner,
  Tab,
  TabList,
  Text,
  tokens,
} from '@fluentui/react-components';
import Header from './Header';
import TopActionBar from './TopActionBar';
import AnalysisTab from './AnalysisTab';
import RedliningTab from './RedliningTab';
import DraftingTab from './DraftingTab';
import DiscussTab from './DiscussTab';
import TemplatesTab from './TemplatesTab';
import TrackChangesPrompt, { TrackChangesAction } from './TrackChangesPrompt';
import UpgradePrompt from './UpgradePrompt';
import { useAppearance } from '../appearance';
import { useUpgradeNavigation } from '../useUpgradeNavigation';
import { applyToSelection, enableTrackChanges, isTrackChangesOn } from '../../word/selection';
import { ApplyOperation } from '../../services/analysis';
import { getSkipTrackChangesPrompt, setSkipTrackChangesPrompt } from '../../word/documentSettings';
import {
  clearPendingIntent,
  PENDING_INTENT_STORAGE_KEY,
  readPendingIntent,
} from '../../services/pendingIntent';
import { AuthUser, getStoredUser, onSessionEnded } from '../../services/auth';
import { appUrl } from '../../services/navigation';
import { isTeamOnFreePlan } from '../../services/billing';
import { openExternal } from '../../services/externalLink';

/* global console */

interface AppProps {
  title: string;
}

type TabValue = 'analysis' | 'redlining' | 'drafting' | 'discuss' | 'templates';

type PendingRewrite = {
  text: string;
  operation: ApplyOperation;
  resolve: (applied: boolean) => void;
};

const useStyles = makeStyles({
  root: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: 'hidden',
  },
  // Font size — uses CSS `zoom` to scale all content uniformly. WebKit-supported
  // (Word for Mac uses WKWebView), works in WebView2 (Word for Windows) too.
  fontSmall: { zoom: 0.9 },
  fontLarge: { zoom: 1.3 },
  body: {
    flex: 1,
    minHeight: 0, // critical: lets flex children scroll instead of overflowing
    padding: '12px 16px',
    display: 'flex',
    flexDirection: 'column',
  },
  tabs: {
    marginBottom: '12px',
    flexShrink: 0,
  },
  errorBar: {
    marginBottom: '10px',
    flexShrink: 0,
  },
  tabContent: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  tabContentNoScroll: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  signedOutState: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '40px 16px',
    textAlign: 'center',
  },
  signedOutTitle: {
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },
  signedOutText: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    maxWidth: '320px',
    textAlign: 'center',
  },
  signedOutActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '8px',
  },
  gateLoading: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

const App: React.FC<AppProps> = ({ title }) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<TabValue>('analysis');
  const [user, setUser] = useState<AuthUser | null>(() => getStoredUser());
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [pendingRewrite, setPendingRewrite] = useState<PendingRewrite | null>(null);
  const { fontSize } = useAppearance();
  const [applyError, setApplyError] = useState<string | null>(null);
  // Redlining edit-type id dispatched from the context-menu handler (or null).
  const [pendingRedlineId, setPendingRedlineId] = useState<string | null>(null);
  // null while unknown (loading, or signed out). Set from the upfront Free-plan
  // check after sign-in, and flipped to true by any tab that hits a plan/quota
  // limit mid-session — either way, collapses the whole tab UI to one upgrade screen.
  const [showUpgradeGate, setShowUpgradeGate] = useState<boolean | null>(null);
  const { handleUpgradeClick, upgradeLoading } = useUpgradeNavigation(user, setUser);

  // A service call can discover mid-session that the refresh token is dead (expired, or the
  // account was revoked). Drop to the signed-out screen so the user is prompted to sign in
  // again, rather than leaving a signed-in UI whose every action now fails.
  useEffect(() => onSessionEnded(() => setUser(null)), []);

  useEffect(() => {
    if (!user) {
      setShowUpgradeGate(null);
      return undefined;
    }
    let cancelled = false;
    isTeamOnFreePlan(user.teamSlug, user.accessToken)
      .then(free => {
        if (!cancelled) setShowUpgradeGate(free);
      })
      .catch(e => {
        console.log('Failed to load team plan:', e);
        // Fail open — per-action checks still enforce the real limit server-side.
        if (!cancelled) setShowUpgradeGate(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Pick up context-menu handoffs. On mount, drain any intent queued before
  // the pane loaded. While open, listen for localStorage writes from the
  // commands.html iframe — same origin, so `storage` events fire here.
  useEffect(() => {
    const consume = () => {
      const intent = readPendingIntent();
      if (!intent) return;
      clearPendingIntent();
      if (intent.type === 'redline') {
        setActiveTab('redlining');
        setPendingRedlineId(intent.editTypeId);
      }
    };

    consume();

    const onStorage = (e: StorageEvent) => {
      if (e.key === PENDING_INTENT_STORAGE_KEY) consume();
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const clearPendingRedline = useCallback(() => setPendingRedlineId(null), []);

  // Any tab's action can discover the plan-limit gate mid-session (e.g. after a
  // downgrade) — collapse straight to the same upgrade screen the upfront check
  // would have shown, instead of a tab-local prompt sitting above stale controls.
  const handlePlanLimitHit = useCallback(() => setShowUpgradeGate(true), []);

  const performApply = useCallback(async (text: string, operation: ApplyOperation) => {
    try {
      await applyToSelection(text, operation);
      setApplyError(null);
    } catch (e) {
      setApplyError(e instanceof Error ? e.message : 'Could not apply change.');
    }
  }, []);

  const handleApply = useCallback(
    async (text: string, operation: ApplyOperation = 'replace'): Promise<boolean> => {
      setApplyError(null);
      try {
        const trackingOn = await isTrackChangesOn();
        if (trackingOn) {
          await performApply(text, operation);
          return true;
        }
        if (getSkipTrackChangesPrompt()) {
          await performApply(text, operation);
          return true;
        }
        // Track-changes prompt path — wait for the user's decision and resolve
        // accordingly so callers (e.g. AnalysisTab) can know if it actually applied.
        return new Promise<boolean>(resolve => {
          setPendingRewrite({ text, operation, resolve });
        });
      } catch (e) {
        setApplyError(e instanceof Error ? e.message : 'Could not check Track Changes state.');
        return false;
      }
    },
    [performApply],
  );

  const handlePromptResolve = useCallback(
    async (action: TrackChangesAction, dontAskAgain: boolean) => {
      const pending = pendingRewrite;
      setPendingRewrite(null);

      if (dontAskAgain) {
        try {
          await setSkipTrackChangesPrompt(true);
        } catch (e) {
          console.log('Failed to persist skip flag:', e);
        }
      }

      if (!pending) return;
      if (action === 'cancel') {
        pending.resolve(false);
        return;
      }

      try {
        if (action === 'enable') {
          await enableTrackChanges();
        }
        await performApply(pending.text, pending.operation);
        pending.resolve(true);
      } catch (e) {
        setApplyError(e instanceof Error ? e.message : 'Could not apply change.');
        pending.resolve(false);
      }
    },
    [pendingRewrite, performApply],
  );

  return (
    <div
      className={`${styles.root} ${
        fontSize === 'small' ? styles.fontSmall : fontSize === 'large' ? styles.fontLarge : ''
      }`}
    >
      <Header
        lightLogo="assets/signus-logo.png"
        darkLogo="assets/signus-logo-dark.png"
        title={title}
        user={user}
        onAuthChange={setUser}
        settingsOpen={settingsOpen}
        onSettingsOpenChange={setSettingsOpen}
      />
      {!user ? (
        <div className={styles.signedOutState}>
          <Text className={styles.signedOutTitle}>Sign in to use Signus</Text>
          <Text className={styles.signedOutText}>
            Log in or create a free account to analyze, redline, draft, and discuss your documents.
          </Text>
          <div className={styles.signedOutActions}>
            <Button appearance="primary" onClick={() => setSettingsOpen(true)}>
              Log in
            </Button>
            <Button appearance="secondary" onClick={() => openExternal(appUrl('join'))}>
              Create account
            </Button>
          </div>
        </div>
      ) : showUpgradeGate === null ? (
        <div className={styles.gateLoading}>
          <Spinner size="medium" />
        </div>
      ) : showUpgradeGate ? (
        <div className={styles.body}>
          <UpgradePrompt variant="plan" onUpgrade={handleUpgradeClick} loading={upgradeLoading} />
        </div>
      ) : (
        <>
          <TopActionBar user={user} onAuthChange={setUser} />
          <div className={styles.body}>
            <TabList
              className={styles.tabs}
              selectedValue={activeTab}
              onTabSelect={(_, data) => setActiveTab(data.value as TabValue)}
            >
              <Tab value="analysis">Analyze</Tab>
              <Tab value="redlining">Redline</Tab>
              <Tab value="drafting">Draft</Tab>
              <Tab value="discuss">Discuss</Tab>
              <Tab value="templates">Templates</Tab>
            </TabList>

            {applyError && (
              <MessageBar className={styles.errorBar} intent="error">
                <MessageBarBody>{applyError}</MessageBarBody>
              </MessageBar>
            )}

            {/* Discuss owns its own scroll
                (input pinned to bottom); other tabs scroll normally inside the body. */}
            <div
              className={styles.tabContentNoScroll}
              style={{ display: activeTab === 'discuss' ? 'flex' : 'none' }}
            >
              <DiscussTab user={user} onAuthChange={setUser} onPlanLimitHit={handlePlanLimitHit} />
            </div>
            <div
              className={styles.tabContent}
              style={{ display: activeTab === 'analysis' ? 'flex' : 'none' }}
            >
              <AnalysisTab onApply={handleApply} onPlanLimitHit={handlePlanLimitHit} />
            </div>
            <div
              className={styles.tabContent}
              style={{ display: activeTab === 'redlining' ? 'flex' : 'none' }}
            >
              <RedliningTab
                onApply={handleApply}
                pendingEditTypeId={pendingRedlineId}
                onPendingHandled={clearPendingRedline}
                user={user}
                onPlanLimitHit={handlePlanLimitHit}
              />
            </div>
            <div
              className={styles.tabContent}
              style={{ display: activeTab === 'drafting' ? 'flex' : 'none' }}
            >
              <DraftingTab user={user} onAuthChange={setUser} onPlanLimitHit={handlePlanLimitHit} />
            </div>
            <div
              className={styles.tabContent}
              style={{ display: activeTab === 'templates' ? 'flex' : 'none' }}
            >
              <TemplatesTab
                user={user}
                onAuthChange={setUser}
                onRequestLogin={() => setSettingsOpen(true)}
              />
            </div>
          </div>
        </>
      )}

      <TrackChangesPrompt open={pendingRewrite !== null} onResolve={handlePromptResolve} />
    </div>
  );
};

export default App;
